import { NavLink } from "react-router-dom";

import { SummaryCard } from "../components/SummaryCard";
import { useContent } from "../hooks/useContent";
import { PageContainer } from "../layout/PageContainer";
import { api } from "../lib/api";
import { DetailCardGrid, DetailMetricGrid, formatCurrency } from "./DeepDiveParts";

export function ZoningPage() {
  const { data, loading, error } = useContent("zoning-page", api.getZoning);

  return (
    <PageContainer
      eyebrow="Supporting Detail - Site & Zoning"
      title="Zoning and STR detail for the Site & Zoning surface"
      lede="This route is the regulatory supporting layer beneath Site & Zoning. It keeps the zoning, STR, and compliance details available without presenting itself as a separate top-level investor entry point."
    >
      {loading ? <div className="loading-panel shimmer-card">Loading zoning summary...</div> : null}
      {error ? <div className="error-panel">{error}</div> : null}
      {data ? (
        <div className="stack-xl">
          <DetailMetricGrid
            items={[
              { title: "Registration", value: data.keyFacts.strRegistrationRequired ? "Required" : "Not required", detail: formatCurrency(data.keyFacts.strRegistrationFee) },
              { title: "Penalty", value: formatCurrency(data.keyFacts.failureToRegisterPenalty), detail: "Failure-to-register civil penalty" },
              { title: "Parking", value: data.keyFacts.offStreetParkingRequired ? "Required" : "Not required", detail: "Current STR read" },
              { title: "Zoning", value: data.keyFacts.zoning, detail: data.keyFacts.zoningDescription }
            ]}
          />

          <DetailCardGrid
            items={[
              {
                eyebrow: "Regulatory Overview",
                title: "Current citywide posture",
                description: data.regulatoryOverview,
                fields: [
                  { label: "Rental Inspection District", value: data.keyFacts.rentalInspectionDistrictStatus },
                  { label: "Occupancy Rule", value: data.keyFacts.occupancyRule },
                  { label: "Related / Unrelated", value: data.keyFacts.relatedOccupancyRule }
                ]
              },
              {
                eyebrow: "Parcel Application",
                title: "What Phase 2 narrowed",
                description: data.parcelApplicationBestRead,
                fields: [
                  { label: "Building Code", value: data.keyFacts.buildingCodeRequirement },
                  { label: "Inspection Approach", value: data.keyFacts.inspectionApproach },
                  { label: "Legal Reference", value: data.keyFacts.legalDescriptionReference }
                ]
              }
            ]}
          />

          <div className="grid-two">
            <SummaryCard eyebrow="Takeaways" title="Why the zoning picture is still supportive" description="The broad framework looks workable; the remaining risk is in lot-specific application, not a district mismatch.">
              <ul className="bullet-list">
                {data.regulatoryTakeaways.map((item) => (
                  <li key={item} className="bullet-item">{item}</li>
                ))}
              </ul>
            </SummaryCard>
            <SummaryCard eyebrow="Open Items" title={`${data.openItems.length} parcel-application checks`} description="These are the remaining zoning-side items still controlling confidence.">
              <ul className="bullet-list">
                {data.openItems.map((item) => (
                  <li key={item} className="bullet-item">{item}</li>
                ))}
              </ul>
            </SummaryCard>
          </div>

          <SummaryCard eyebrow="Conclusion" title={data.status} description={data.conclusion} />

          <SummaryCard
            eyebrow="Parent Surface"
            title="Return to Site & Zoning for the full land and regulatory read"
            description="Use this page for parcel-application and STR-rule detail. Use the parent Site & Zoning surface for the combined investor-facing diligence posture."
          >
            <div className="row-wrap gap-sm overview-go-deeper-actions">
              <NavLink to="/site-zoning" className="link-button link-button-secondary">
                Back to Site & Zoning
              </NavLink>
            </div>
          </SummaryCard>
        </div>
      ) : null}
    </PageContainer>
  );
}
